import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next/src';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import ShortId from 'shortid';
import Dialog from '@hobs/components/Dialog';
import Dropdown from '@hobs/components/Dropdown';
import NumericInput from '@hobs/components/NumericInput';
import DateInput from '@hobs/components/DateInput';
import BluePanel from '@hobs/components/BluePanel';
import ObjectPath from 'object-path';
import Autocomplete from '@hobs/components/Autocomplete';

import Toast from '@hobs/components/Toast';
import Switch from '@hobs/components/Switch';
import Textarea from '@hobs/components/Textarea';
import InputLabel from '@hobs/components/InputLabel';
import { Formik, Form, Field } from 'formik';
import Input from '@hobs/components/Input';
import { RaisedButton, GhostButton } from '@hobs/components/Buttons';
import Alert from '@hobs/components/Alert';
import { queryLocations } from '@hobs/actions/locations';
import {
  createChangeRequest,
  calculateByDays,
  clearCalculateByDays
} from '@hobs/actions/tickets';
import { dateToInitialMonth } from '@hobs/utils/dateToInitialMonth';
import Quantity from '@hobs/components/Quantity';
import Money from '@hobs/components/Money';
import CalculateByDays from '@hobs/components/CalculateByDays';

import './ChangeRequestCreateDialog.scss';

const defaultState = {
  error: null,
  calculatedCostValues: {}
};

class ChangeRequestCreateDialog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = defaultState;

    const { t } = props;
    this.validationSchema = Yup.object().shape({
      unitId: Yup.string().required(t('Unit is required')),
      unitOfMeasureId: Yup.string().required(t('UoM is required')),
      dateFrom: Yup.date()
        .required(t('From date is required'))
        .typeError(t('From date is required'))
        .nullable(),
      requestedQuantity: Yup.number()
        .min(0, t('Quantity must be greater than 0'))
        .required(t('Quantity is required'))
        .typeError(t('Invalid number')),
      comment: Yup.string()
        .max(1500, t('Comment cannot be longer than 1500 symbols'))
        .nullable(),
      locationName: Yup.string()
        .required(t('Location is required'))
        .nullable(),
      locationNameFrom: Yup.string().when('isTransportation', {
        is: true,
        then: Yup.string()
          .required(t('Location is required'))
          .nullable(),
        otherwise: Yup.string().nullable()
      })
    });
  }

  onDialogOpening = () => {
    const { serviceRequest } = this.props;

    this.calculateCosts(serviceRequest);
  };

  onDialogClose = () => {
    const { closeDialog } = this.props;

    closeDialog();
  };

  onDialogClosed = () => {
    this.resetState();
  };

  setError(message) {
    this.setState({ error: message });
  }

  resetState() {
    this.setState(defaultState);
  }

  onLocationQueryChange = query => {
    this.props.queryLocations(query);
  };

  calculateCosts(values) {
    if (!values.dateFrom || !values.requestedQuantity) {
      return;
    }

    const currentState = this.state.calculatedCostValues;
    if (
      currentState.dateFrom !== values.dateFrom ||
      currentState.dateTo !== values.dateTo ||
      currentState.quantity !== values.requestedQuantity ||
      currentState.isCalculatedByDays !== values.isCalculatedByDays
    ) {
      const data = {
        rateFrom: values.reateFrom,
        rateTo: values.reateTo,
        quantity: values.requestedQuantity,
        dateFrom: values.dateFrom,
        dateTo: values.dateTo,
        isCalculatedByDays: values.isCalculatedByDays
      };

      this.props.calculateByDays(data);
      this.setState({ calculatedCostValues: data });
    }
  }

  toAutocompleteEntity(data, valueIndex = 'code', titleIndex = 'name') {
    return data
      ? data.map(item => ({
          value: item[valueIndex],
          title: item[titleIndex],
          original: item
        }))
      : [];
  }

  submitConfirm = (values, { setSubmitting, setErrors }) => {
    const { createChangeRequest, serviceRequestId, t, reload } = this.props;

    createChangeRequest(serviceRequestId, values).then(response => {
      if (response.success) {
        Toast.success(t('Change request submitted successfully'));
        reload();
        this.onDialogClose();
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [error.target]: error.message[0]
          };
        }, {});
        if (errors) {
          setErrors(errors);
        }
      } else if (response.payload) {
        this.setError(response.payload.message);
      }

      setSubmitting(false);
    });
  };

  getInitialActivityId = () => {
    if (this.props.serviceRequest && this.props.serviceRequest.activityId) {
      return this.props.serviceRequest.activityId;
    }

    return null;
  };

  getInitialActivityName = () => {
    if (this.props.serviceRequest && this.props.serviceRequest.activityId) {
      return this.props.serviceRequest.activityName;
    }

    return null;
  };

  toActivityEntity(activities) {
    return activities.map(activity => {
      return {
        name: activity.name,
        activityId: activity.activityId,
        isDefault: activity.isDefault
      };
    });
  }

  render() {
    const {
      t,
      isDialogOpen,
      calculateByDaysData,
      query,
      unitsOptions,
      activities,
      serviceRequest,
      uomsOptions,
      currentExercise
    } = this.props;
    const { error } = this.state;

    // If we don't have values - DO not render Dialog
    if (Object.keys(serviceRequest).length === 0) {
      return null;
    }

    return (
      <Dialog
        title={t('Change request information')}
        isOpen={isDialogOpen}
        onClose={this.onDialogClose}
        onClosed={this.onDialogClosed}
        onOpening={this.onDialogOpening}
        className="change-request"
      >
        <Formik
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={this.validationSchema}
          enableReinitialize={true}
          initialValues={{
            ...serviceRequest,
            activityId: this.getInitialActivityId(),
            activityName: this.getInitialActivityName(),
            comment: null
          }}
          onSubmit={this.submitConfirm}
        >
          {({ setFieldValue, values, isSubmitting }) => (
            <Form noValidate={true}>
              {error && <Alert type="error">{error}</Alert>}
              {this.calculateCosts(values)}
              <div>
                <div className="change-request--row">
                  <div className="change-request--column">
                    <div className="change-request--row">
                      <Field
                        setFieldValue={setFieldValue}
                        label={t('To Unit')}
                        name="unitId"
                        disabled={true}
                        component={Dropdown}
                        wrapperClass="change-request-dropdown"
                      >
                        {unitsOptions}
                      </Field>
                    </div>
                    <div className="change-request--row">
                      <div className="change-request--column">
                        <Field
                          setFieldValue={setFieldValue}
                          name="requestedQuantity"
                          placeholder="Quantity"
                          component={NumericInput}
                          min={0}
                        >
                          {t('Req. Quantity')}
                        </Field>
                      </div>
                      <div className="change-request--column">
                        <Field
                          setFieldValue={setFieldValue}
                          label={t('UoM')}
                          name="unitOfMeasureId"
                          disabled={true}
                          placeholder="UoM"
                          component={Dropdown}
                          wrapperClass="change-request-dropdown"
                        >
                          {uomsOptions}
                        </Field>
                      </div>
                    </div>
                    <div className="change-request--row">
                      <div className="change-request--column">
                        <Field
                          setFieldValue={setFieldValue}
                          name="dateFrom"
                          component={DateInput}
                          wrapperClass="change-request-date"
                        >
                          {t('From date')}
                        </Field>
                      </div>
                      <div className="change-request--column">
                        <Field
                          setFieldValue={setFieldValue}
                          name="dateTo"
                          component={DateInput}
                          initialMonth={dateToInitialMonth(
                            values.dateFrom,
                            values.dateTo
                          )}
                          wrapperClass="change-request-date"
                        >
                          {t('To date')}
                        </Field>
                      </div>
                    </div>
                  </div>
                  <div className="change-request--column totals">
                    <BluePanel>
                      <div className="delivery-information--cost-panel">
                        <div className="item">
                          {t('Total quantity')}
                          <strong>
                            {calculateByDaysData ? (
                              <Quantity amount={calculateByDaysData.quantity} />
                            ) : (
                              '-'
                            )}
                          </strong>
                        </div>

                        <div className="item">
                          {t('Est. total cost')}
                          <span> ({currentExercise.currency.name})</span>
                          {calculateByDaysData ? (
                            <strong>
                              <Money
                                amount={ObjectPath.get(
                                  calculateByDaysData,
                                  'rateFrom',
                                  ''
                                )}
                              />
                              {(calculateByDaysData.rateFrom &&
                                calculateByDaysData.rateTo) ||
                              (!calculateByDaysData.rateFrom &&
                                !calculateByDaysData.rateTo)
                                ? ' - '
                                : ''}
                              <Money
                                amount={ObjectPath.get(
                                  calculateByDaysData,
                                  'rateTo',
                                  ''
                                )}
                              />
                            </strong>
                          ) : (
                            '-'
                          )}
                        </div>
                      </div>
                    </BluePanel>
                  </div>
                </div>
                <CalculateByDays
                  name="isCalculatedByDays"
                  component={Switch}
                  checked={values.isCalculatedByDays}
                  setFieldValue={setFieldValue}
                  differenceInDays={
                    calculateByDaysData ? calculateByDaysData.days : null
                  }
                  isInline={true}
                />
                {activities && activities.length !== 0 && (
                  <Field
                    setFieldValue={setFieldValue}
                    label={t('Activity')}
                    name="activityId"
                    component={Dropdown}
                    disabled={true}
                    defaultValue={ShortId.generate()}
                    onChange={event => {
                      setFieldValue(
                        'activityName',
                        activities.find(x => x.activityId == event.target.value)
                          .name
                      );
                    }}
                  >
                    {!values.activityId && (
                      <option value="">{t('No activity selected')}</option>
                    )}
                    {values.activityId && (
                      <option value={values.activityId}>
                        {t(values.activityName)}
                      </option>
                    )}
                  </Field>
                )}
                {!values.isTransportation && (
                  <div className="change-request--row">
                    <div className="change-request--column">
                      <Field
                        label={t('Location')}
                        placeholder="Location"
                        name="locationName"
                        items={this.toAutocompleteEntity(query)}
                        onItemSelect={item => {
                          setFieldValue(
                            'locationName',
                            ObjectPath.get(item, 'original.name', null)
                          );
                          setFieldValue(
                            'regionId',
                            ObjectPath.get(
                              item,
                              'original.region.regionId',
                              null
                            )
                          );
                          setFieldValue(
                            'regionName',
                            ObjectPath.get(item, 'original.region.name', null)
                          );
                          setFieldValue(
                            'site',
                            ObjectPath.get(item, 'original.site', null)
                          );
                          setFieldValue(
                            'latitude',
                            ObjectPath.get(item, 'original.latitude', null)
                          );
                          setFieldValue(
                            'longitude',
                            ObjectPath.get(item, 'original.longtitude', null)
                          );
                        }}
                        noResultsMessage={t('Location not found')}
                        onQueryChange={this.onLocationQueryChange}
                        component={Autocomplete}
                        defaultQuery={values.locationName}
                      />
                    </div>
                    <div className="change-request--column">
                      <Field
                        placeholder={t('Location comment')}
                        name="locationComment"
                        setFieldValue={setFieldValue}
                        component={Input}
                      >
                        {t('Location comment')}
                      </Field>
                    </div>
                  </div>
                )}
                {values.isTransportation && (
                  <>
                    <div className="change-request--row">
                      <div className="change-request--column">
                        <Field
                          label={t('Pickup location')}
                          placeholder="Location"
                          name="locationNameFrom"
                          items={this.toAutocompleteEntity(query)}
                          onItemSelect={item => {
                            setFieldValue(
                              'locationNameFrom',
                              ObjectPath.get(item, 'original.name', null)
                            );
                            setFieldValue(
                              'regionIdFrom',
                              ObjectPath.get(
                                item,
                                'original.region.regionId',
                                null
                              )
                            );
                            setFieldValue(
                              'regionNameFrom',
                              ObjectPath.get(item, 'original.region.name', null)
                            );
                            setFieldValue(
                              'siteFrom',
                              ObjectPath.get(item, 'original.site', null)
                            );
                            setFieldValue(
                              'latitudeFrom',
                              ObjectPath.get(item, 'original.latitude', null)
                            );
                            setFieldValue(
                              'longitudeFrom',
                              ObjectPath.get(item, 'original.longtitude', null)
                            );
                          }}
                          noResultsMessage={t('Location not found')}
                          onQueryChange={this.onLocationQueryChange}
                          component={Autocomplete}
                          defaultQuery={values.locationNameFrom}
                        />
                      </div>
                      <div className="change-request--column">
                        <Field
                          placeholder={t('Location comment')}
                          name="locationCommentFrom"
                          setFieldValue={setFieldValue}
                          component={Input}
                        >
                          {t('Pickup location comment')}
                        </Field>
                      </div>
                    </div>
                    <div className="change-request--row">
                      <div className="change-request--column">
                        <Field
                          label={t('Delivery location')}
                          placeholder="Location"
                          name="locationName"
                          items={this.toAutocompleteEntity(query)}
                          onItemSelect={item => {
                            setFieldValue(
                              'locationName',
                              ObjectPath.get(item, 'original.name', null)
                            );
                            setFieldValue(
                              'regionId',
                              ObjectPath.get(
                                item,
                                'original.region.regionId',
                                null
                              )
                            );
                            setFieldValue(
                              'regionName',
                              ObjectPath.get(item, 'original.region.name', null)
                            );
                            setFieldValue(
                              'site',
                              ObjectPath.get(item, 'original.site', null)
                            );
                            setFieldValue(
                              'latitude',
                              ObjectPath.get(item, 'original.latitude', null)
                            );
                            setFieldValue(
                              'longitude',
                              ObjectPath.get(item, 'original.longtitude', null)
                            );
                          }}
                          noResultsMessage={t('Location not found')}
                          onQueryChange={this.onLocationQueryChange}
                          component={Autocomplete}
                          defaultQuery={values.locationName}
                        />
                      </div>
                      <div className="change-request--column">
                        <Field
                          placeholder={t('Location comment')}
                          name="locationComment"
                          setFieldValue={setFieldValue}
                          component={Input}
                        >
                          {t('Delivery location comment')}
                        </Field>
                      </div>
                    </div>
                  </>
                )}
                <div className="change-request--row">
                  <Field
                    name="comment"
                    setFieldValue={setFieldValue}
                    label={t('Comment (why change is needed)')}
                    component={Textarea}
                  />
                </div>
                <div className="change-request--buttons">
                  <GhostButton
                    onClick={() => {
                      this.onDialogClose();
                    }}
                  >
                    {t('Cancel')}
                  </GhostButton>
                  <RaisedButton type="submit" disabled={isSubmitting}>
                    {t('Submit')}
                  </RaisedButton>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    );
  }
}

ChangeRequestCreateDialog.propTypes = {
  t: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  isDialogOpen: PropTypes.bool,
  closeDialog: PropTypes.func,
  calculateByDaysData: PropTypes.object,
  calculateByDays: PropTypes.func,
  queryLocations: PropTypes.func,
  onQueryChange: PropTypes.func,
  serviceRequest: PropTypes.object,
  unitsOptions: PropTypes.array,
  query: PropTypes.array,
  clearCalculateByDays: PropTypes.func,
  createChangeRequest: PropTypes.func,
  serviceRequestId: PropTypes.string,
  uomsOptions: PropTypes.array,
  activities: PropTypes.array,
  reload: PropTypes.func,
  currentExercise: PropTypes.object
};

const mapStateToProps = state => ({
  calculateByDaysData: state.tickets.calculateByDays,
  query: state.locations.query,
  currentExercise: state.exercise.current
});

const mapDispatchToProps = {
  calculateByDays,
  queryLocations,
  clearCalculateByDays,
  createChangeRequest
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(ChangeRequestCreateDialog))
);
